import type { MatchState, PlayerSpatialMetrics } from '../match/MatchState';
import type { MovementState, PlayerRuntimeState } from '../match/PlayerRuntimeState';
import { PITCH_GEOMETRY } from '../match/PitchGeometry';
import { distance } from '../match/Vector2';
import { ROLE_CONSTRAINTS } from './RoleConstraints';

export interface PlayerSpatialSummary {
  id: string;
  playerId: string;
  sourcePlayerId: string;
  teamSide: 0 | 1;
  positionType: PlayerRuntimeState['positionType'];
  role: string;
  sampledSeconds: number;
  offensiveZonePercentage: number;
  extremeWidePercentage: number;
  ownHalfPercentage: number;
  averageAnchorDistance: number;
  outsidePreferredZonePercentage: number;
  outsidePreferredZonePercentageByMovementState: Record<MovementState, number>;
  unjustifiedOutsidePreferredZonePercentage: number;
  zoneExcursions: number;
}

const hasTemporaryTacticalReason = (player: PlayerRuntimeState, matchSecond: number): boolean => (
  player.intent.reason !== 'KEEP_SHAPE'
  && player.intent.reason !== 'RETURN_TO_SHAPE'
  && (player.intent.expiresAt === undefined || matchSecond < player.intent.expiresAt)
);

const emptyMovementStateDurations = (): Record<MovementState, number> => ({
  HOLDING_SHAPE: 0,
  SUPPORTING_ATTACK: 0,
  PRESSING: 0,
  MARKING: 0,
  TRACKING_RUN: 0,
  RECOVERING: 0,
  MAKING_RUN: 0,
  CARRYING_BALL: 0,
  CHASING_LOOSE_BALL: 0,
  GOALKEEPER_POSITIONING: 0,
});

export class SpatialTelemetry {
  private readonly metricsById = new Map<string, PlayerSpatialMetrics>();

  createMetric(player: PlayerRuntimeState, startedAt: number): PlayerSpatialMetrics {
    const metric: PlayerSpatialMetrics = {
      id: player.spatialMetricId,
      playerId: player.playerId,
      sourcePlayerId: player.sourcePlayerId ?? player.playerId,
      teamSide: player.teamSide,
      positionType: player.positionType,
      role: player.role,
      startedAt,
      sampledSeconds: 0,
      offensiveZoneSeconds: 0,
      extremeWideSeconds: 0,
      ownHalfSeconds: 0,
      anchorDistanceSeconds: 0,
      outsidePreferredZoneSeconds: 0,
      outsidePreferredZoneSecondsByMovementState: emptyMovementStateDurations(),
      unjustifiedOutsidePreferredZoneSeconds: 0,
      zoneExcursions: 0,
      wasOutsidePreferredZone: false,
    };
    this.metricsById.set(metric.id, metric);
    return metric;
  }

  createInitialMetrics(players: readonly PlayerRuntimeState[]): PlayerSpatialMetrics[] {
    this.metricsById.clear();
    return players.map((player) => this.createMetric(player, 0));
  }

  closeMetric(state: MatchState, player: PlayerRuntimeState): void {
    const metric = this.metric(state, player.spatialMetricId);
    if (metric && metric.endedAt === undefined) metric.endedAt = state.time;
  }

  update(state: MatchState, deltaTime: number): void {
    if (deltaTime <= 0) return;
    if (this.metricsById.size !== state.spatialMetrics.length) this.indexMetrics(state);
    for (const player of state.players) {
      const metric = this.metricsById.get(player.spatialMetricId);
      if (!metric || metric.endedAt !== undefined) continue;
      const progress = PITCH_GEOMETRY.relativeProgress(player.teamSide, player.position.x);
      const preferred = ROLE_CONSTRAINTS[player.positionType].homeZone;
      const outsidePreferred = progress < preferred[0] || progress > preferred[1];

      metric.sampledSeconds += deltaTime;
      if (PITCH_GEOMETRY.isInOffensiveZone(player.teamSide, player.position)) metric.offensiveZoneSeconds += deltaTime;
      if (PITCH_GEOMETRY.isExtremeWide(player.position)) metric.extremeWideSeconds += deltaTime;
      if (PITCH_GEOMETRY.isInOwnHalf(player.teamSide, player.position)) metric.ownHalfSeconds += deltaTime;
      metric.anchorDistanceSeconds += distance(player.position, player.homePosition) * deltaTime;
      if (outsidePreferred) {
        metric.outsidePreferredZoneSeconds += deltaTime;
        metric.outsidePreferredZoneSecondsByMovementState[player.movementState] += deltaTime;
        if (!hasTemporaryTacticalReason(player, state.time)) metric.unjustifiedOutsidePreferredZoneSeconds += deltaTime;
        if (!metric.wasOutsidePreferredZone) metric.zoneExcursions++;
      }
      metric.wasOutsidePreferredZone = outsidePreferred;
    }
  }

  private metric(state: MatchState, id: string): PlayerSpatialMetrics | undefined {
    const indexed = this.metricsById.get(id);
    if (indexed) return indexed;
    this.indexMetrics(state);
    return this.metricsById.get(id);
  }

  private indexMetrics(state: MatchState): void {
    this.metricsById.clear();
    for (const metric of state.spatialMetrics) this.metricsById.set(metric.id, metric);
  }
}

export function summarizeSpatialMetric(metric: PlayerSpatialMetrics): PlayerSpatialSummary {
  const duration = metric.sampledSeconds || 1;
  const percentage = (seconds: number) => seconds / duration * 100;
  return {
    id: metric.id,
    playerId: metric.playerId,
    sourcePlayerId: metric.sourcePlayerId,
    teamSide: metric.teamSide,
    positionType: metric.positionType,
    role: metric.role,
    sampledSeconds: metric.sampledSeconds,
    offensiveZonePercentage: percentage(metric.offensiveZoneSeconds),
    extremeWidePercentage: percentage(metric.extremeWideSeconds),
    ownHalfPercentage: percentage(metric.ownHalfSeconds),
    averageAnchorDistance: metric.anchorDistanceSeconds / duration,
    outsidePreferredZonePercentage: percentage(metric.outsidePreferredZoneSeconds),
    outsidePreferredZonePercentageByMovementState: Object.fromEntries(
      Object.entries(metric.outsidePreferredZoneSecondsByMovementState)
        .map(([movementState, seconds]) => [movementState, percentage(seconds)]),
    ) as Record<MovementState, number>,
    unjustifiedOutsidePreferredZonePercentage: percentage(metric.unjustifiedOutsidePreferredZoneSeconds),
    zoneExcursions: metric.zoneExcursions,
  };
}
